import React, { Component } from "react";
import PropTypes from "prop-types";
import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";
import { getOrderSideColor } from "../../../../utils/orderStyles";
import marketdata from "../../../../data/Services/marketdata.jsx";
import { formatNumber } from "../../../../utils/numberFormatting";
import { getNumericFontFamily } from "../../../../utils/fontUtils";

// Simple position price label component for interactive overlays
class PositionLabel extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.calculatePnL = this.calculatePnL.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.position?.quantity !== this.props.position?.quantity ||
      prevProps.position?.price !== this.props.position?.price
    ) {
      if (this.context?.redraw) {
        this.context.redraw();
      }
    }
  }

  componentWillUnmount() {
    // Defer redraw until after child GenericChartComponent has unsubscribed,
    // otherwise redraw() re-draws the stale position on the freshly cleared canvas
    const redraw = this.context?.redraw;
    if (redraw) {
      setTimeout(() => redraw(), 0);
    }
  }

  // Calculate P&L for position (same logic as usePositionsData).
  // Retorna null quando o FOQ do servidor não está disponível: multiplicador é
  // spec do contrato e NUNCA é fabricado no cliente — sem dado, sem R$.
  calculatePnL(position, currentPrice) {
    if (!position || !position.quantity) return 0;

    const foq = marketdata?.getFOQ?.(position.symbol);
    if (foq === null || foq === undefined) return null;

    const { quantity, price } = position;
    const absQ = Math.abs(quantity);
    const entryLot = price * absQ;
    const currentLot = currentPrice * absQ;

    let net = 0;
    if (quantity > 0) net = currentLot - entryLot;
    else if (quantity < 0) net = entryLot - currentLot;

    return net * foq;
  }

  drawOnCanvas(ctx, moreProps) {
    try {
      const { position } = this.props;

      if (!position || !position.quantity || position.quantity === 0) {
        return;
      }

      const {
        chartConfig: { yScale },
      } = moreProps;

      if (!yScale || typeof yScale !== "function" || !position.price) {
        return;
      }

      // Get Y position for the position price
      const y = yScale(position.price);

      // Get current market price for P&L calculation
      let currentPrice = position.price;
      if (marketdata && position.symbol) {
        const lastPrice = marketdata.getLastQuote(position.symbol)?.C;
        if (typeof lastPrice === "number" && isFinite(lastPrice)) {
          currentPrice = lastPrice;
        }
      }

      // Calculate P&L
      const pnl = this.calculatePnL(position, currentPrice);

      // Determine side and get color
      const side = position.quantity > 0 ? "buy" : "sell";
      const sideColor = getOrderSideColor(side);

      // Format the position info with P&L using global formatting functions
      const formattedPrice = formatNumber(position.price, 2); // Position price is average, always 2 decimals
      const qtyText = formatNumber(Math.abs(position.quantity), 0);
      const sideText = side === "buy" ? "COMPRADO" : "VENDIDO";
      const baseText = `${sideText} ${qtyText} @ ${formattedPrice}`;
      // FOQ indisponível (pnl null) → sem badge de R$: preço e quantidade são
      // conhecidos, o dinheiro não — não se exibe valor chutado.
      const pnlDisplayText =
        pnl === null
          ? ""
          : ` (${pnl >= 0 ? `+${formatNumber(pnl, 2)}` : formatNumber(pnl, 2)})`;

      // Set font before measuring text
      const fontFamily = getNumericFontFamily();
      ctx.font = `600 11px ${fontFamily}`;

      // Measure text widths to calculate dynamic rectangle size
      const baseTextWidth = ctx.measureText(baseText).width;
      const pnlTextWidth = ctx.measureText(pnlDisplayText).width;

      // Calculate dynamic label dimensions with padding and stepped sizing
      const textPadding = 8; // Left padding
      const rightPadding = 8; // Right padding
      const calculatedWidth =
        baseTextWidth + pnlTextWidth + textPadding + rightPadding;

      // Round up to next 20px step to prevent constant resizing
      const stepSize = 20;
      const labelWidth = Math.ceil(calculatedWidth / stepSize) * stepSize;
      const labelHeight = 20;
      const labelX = 5; // Left edge with small margin
      const labelY = y - labelHeight / 2;

      // Draw rounded background rectangle with calculated width
      ctx.fillStyle = sideColor;
      const borderRadius = 4; // Match other interactive objects

      // Create rounded rectangle path
      ctx.beginPath();
      ctx.roundRect(labelX, labelY, labelWidth, labelHeight, borderRadius);
      ctx.fill();

      // Draw base text (position info)
      ctx.fillStyle = "white";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";

      const textY = labelY + labelHeight / 2;
      ctx.fillText(baseText, labelX + textPadding, textY);

      // Draw small colored background for P&L (omitido quando FOQ indisponível)
      if (pnlDisplayText) {
        const pnlBgColor = pnl >= 0 ? "#16a34a" : "#dc2626"; // Green for profit, red for loss
        const pnlBgX = labelX + textPadding + baseTextWidth;
        const pnlBgY = labelY + 2;
        const pnlBgWidth = pnlTextWidth + 4;
        const pnlBgHeight = labelHeight - 4;

        ctx.fillStyle = pnlBgColor;
        const pnlBorderRadius = 2; // Smaller radius for P&L badge

        // Create rounded rectangle path for P&L background
        ctx.beginPath();
        ctx.roundRect(pnlBgX, pnlBgY, pnlBgWidth, pnlBgHeight, pnlBorderRadius);
        ctx.fill();

        // Draw P&L text in white over the colored background
        ctx.fillStyle = "white";
        ctx.fillText(pnlDisplayText, pnlBgX + 2, textY);
      }

      // Reset canvas state
      ctx.textAlign = "start";
      ctx.textBaseline = "alphabetic";
    } catch (error) {
      console.warn("PositionLabel drawOnCanvas error:", error);
    }
  }

  renderSVG() {
    // Only implement canvas rendering for now
    return null;
  }

  render() {
    return (
      <GenericChartComponent
        clip={true}
        svgDraw={this.renderSVG}
        canvasDraw={this.drawOnCanvas}
        canvasToDraw={getMouseCanvas}
        drawOn={[
          "mousemove",
          "mouseleave",
          "pan",
          "drag",
          "draw",
          "zoom",
          "xAxisZoom",
          "resize",
        ]}
      />
    );
  }
}

PositionLabel.propTypes = {
  position: PropTypes.shape({
    quantity: PropTypes.number,
    price: PropTypes.number,
    basis: PropTypes.number,
    symbol: PropTypes.string,
  }),
  symbol: PropTypes.string.isRequired,
};

PositionLabel.defaultProps = {
  position: null,
};

PositionLabel.contextTypes = {
  redraw: PropTypes.func,
};

export default PositionLabel;
